import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '../../../lib/toast';
import { appPath } from '../../../routes/paths';
import { useAuthStore } from '../../auth/store/authStore';
import { applyAuthSession } from '../../auth/utils/applyAuthSession';
import { getCurrentUser } from '../../auth/api/auth';
import { enterTenantSupportViewApi } from '../api/supportView';
import { useSupportViewStore } from '../store/supportViewStore';

export function useEnterTenantSupportView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);
  const setActiveSession = useSupportViewStore((state) => state.setActiveSession);

  return useMutation({
    mutationFn: enterTenantSupportViewApi,
    onSuccess: async (session) => {
      const { token, user, csrfToken } = useAuthStore.getState();

      if (!token || !user || !csrfToken) {
        toast.error('Unable to start support view. Please sign in again.');
        return;
      }

      const expiresAtMs = Date.now() + session.expiresInSeconds * 1000;

      setActiveSession({
        operatorAccessToken: token,
        operatorUser: user,
        companyName: session.companyName,
        tenantId: session.tenantId,
        expiresAtMs,
      });

      applyAuthSession(session.accessToken, csrfToken, session.expiresInSeconds);

      const meResult = await getCurrentUser();
      if (meResult.success) {
        setUser(meResult.user);
      }

      await queryClient.cancelQueries();
      queryClient.clear();

      toast.success(`Switched to ${session.companyName}. Impersonation mode is active.`);
      navigate(appPath());
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Unable to enter support view.');
    },
  });
}

export function useExitTenantSupportView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);
  const clearSupportView = useSupportViewStore((state) => state.clear);
  const operatorAccessToken = useSupportViewStore((state) => state.operatorAccessToken);
  const csrfToken = useAuthStore((state) => state.csrfToken);

  return useMutation({
    mutationFn: async () => undefined,
    onSuccess: async () => {
      if (operatorAccessToken && csrfToken) {
        applyAuthSession(operatorAccessToken, csrfToken);
      }

      clearSupportView();

      const meResult = await getCurrentUser();
      if (meResult.success) {
        setUser(meResult.user);
      } else {
        setUser(null);
      }

      await queryClient.cancelQueries();
      queryClient.clear();

      toast.success('Exited impersonation mode.');
      navigate(appPath('/host'));
    },
  });
}
